import React from 'react';
import {
  ALERTS_DOC_URL,
  API_PLAYGROUND_DOC_URL,
  CLOUD_ACCOUNTS_DOC_URL,
  DATA_EXPLORER_DOC_URL,
  DATA_MODELS_DOC_URL,
  DATA_ONBOARDING_DOC_URL,
  DESTINATIONS_DOC_URL,
  DETECTION_GLOBAL_HELPERS_DOC_URL,
  DETECTION_PACKS_DOC_URL,
  ENRICHMENT_PROVIDERS_DOC_URL,
  PageNameEnum,
  PANTHER_DOCS_LINK,
  PUBLIC_API_DOC_URL,
  QUERY_BUILDER_DOC_URL,
  QUERY_HISTORY_DOC_URL,
  SAVED_QUERIES_CRON_DOC_URL,
  SCHEMAS_DOC_URL,
  USER_ROLES_DOC_URL,
  WRITING_DETECTIONS_DOC_URL,
} from 'Source/constants';
import urls from 'Source/urls';
import { FlexProps } from 'pounceV2';

export const PageHeaderData: Record<
  string,
  {
    pageTitle: PageNameEnum;
    pageDescription?: string | React.ReactElement;
    documentationLink?: string;
    isSticky?: boolean;
    headerStyleProps?: FlexProps;
    pageHeaderStyleProps?: FlexProps;
  }
> = {
  [urls.alerts.list()]: {
    pageTitle: PageNameEnum.Alerts,
    pageDescription:
      'Alerts generated by Panther detections, error messages, and system health notifications.',
    documentationLink: ALERTS_DOC_URL,
  },
  [urls.ai.scheduledPrompts.list()]: {
    pageTitle: PageNameEnum.AIScheduledPrompts,
    pageDescription: 'Automate recurring Panther AI investigations on a schedule.',
  },
  // investigate menu pages
  [urls.investigate.search()]: {
    pageTitle: PageNameEnum.Search,
    pageDescription: 'Explore normalized Panther data with our interactive search tool.',
    documentationLink: QUERY_BUILDER_DOC_URL,
  },
  [urls.investigate.dataExplorer()]: {
    pageTitle: PageNameEnum.DataExplorer,
    pageDescription: 'Search and explore your data.',
    documentationLink: DATA_EXPLORER_DOC_URL,
  },
  [urls.investigate.savedSearches.list()]: {
    pageTitle: PageNameEnum.SavedSearches,
    pageDescription: 'View saved Searches and Data Explorer queries.',
    documentationLink: SAVED_QUERIES_CRON_DOC_URL,
  },
  [urls.investigate.searchHistory()]: {
    pageTitle: PageNameEnum.SearchHistory,
    pageDescription: 'View the past 7 days of Searches and Data Explorer SQL queries.',
    documentationLink: QUERY_HISTORY_DOC_URL,
  },
  [urls.investigate.cloudResources.list()]: {
    pageTitle: PageNameEnum.CloudResources,
    pageDescription: 'View results of Cloud Security Scanning policy checks.',
    documentationLink: CLOUD_ACCOUNTS_DOC_URL,
  },
  // build menu pages
  [urls.build.detections.list()]: {
    pageTitle: PageNameEnum.Detections,
    pageDescription: 'Analyze data, run queries, and trigger alerts on suspicious behavior.',
    documentationLink: WRITING_DETECTIONS_DOC_URL,
  },
  [urls.build.globalHelpers.list()]: {
    pageTitle: PageNameEnum.GlobalHelpers,
    pageDescription: 'Extract repeated code into helper functions for writing detections.',
    documentationLink: DETECTION_GLOBAL_HELPERS_DOC_URL,
  },
  [urls.build.globalHelpers.create()]: {
    pageTitle: PageNameEnum.GlobalHelpersNew,
  },
  [urls.build.packs.list()]: {
    pageTitle: PageNameEnum.Packs,
    pageDescription: 'Use Packs to group detections and enable updates via the Panther Console.',
    documentationLink: DETECTION_PACKS_DOC_URL,
  },
  [urls.build.dataModels.list()]: {
    pageTitle: PageNameEnum.DataModels,
    pageDescription:
      'Data Models provide a way to configure a set of unified fields across all log types.',
    documentationLink: DATA_MODELS_DOC_URL,
  },
  [urls.build.dataModels.create()]: {
    pageTitle: PageNameEnum.DataModelNew,
  },
  [urls.dashboard.mitreMapping({})]: {
    pageTitle: PageNameEnum.MitreMapping,
  },
  // configure menu pages
  [urls.configure.logSources.list()]: {
    pageTitle: PageNameEnum.LogSources,
    pageDescription: 'Onboard logs for detection and investigation.',
    documentationLink: DATA_ONBOARDING_DOC_URL,
  },
  [urls.configure.cloudAccounts.list()]: {
    pageTitle: PageNameEnum.CloudAccounts,
    pageDescription:
      'Cloud Security Scanning uses policies to detect risks and misconfigurations in AWS.',
    documentationLink: CLOUD_ACCOUNTS_DOC_URL,
  },
  [urls.configure.cloudAccounts.create()]: {
    pageTitle: PageNameEnum.CloudAccountNew,
    documentationLink: CLOUD_ACCOUNTS_DOC_URL,
  },
  [urls.configure.alertDestinations.list()]: {
    pageTitle: PageNameEnum.AlertDestinations,
    pageDescription: 'View and manage integrations that receive alerts from rules and policies.',
    documentationLink: DESTINATIONS_DOC_URL,
  },
  [urls.configure.schemas.list()]: {
    pageTitle: PageNameEnum.Schemas,
    pageDescription: 'Schemas determine how data is normalized for detection and investigations.',
    documentationLink: SCHEMAS_DOC_URL,
  },
  [urls.configure.enrichmentProviders.list()]: {
    pageTitle: PageNameEnum.EnrichmentProviders,
    pageDescription:
      'Enrichment data helps improve alert fidelity, speed up analyst workflows, and ensure prioritization of the most critical alerts.',
    documentationLink: ENRICHMENT_PROVIDERS_DOC_URL,
  },
  [urls.contentCatalog.browse()]: {
    pageTitle: PageNameEnum.ContentCatalog,
    pageDescription: 'Browse and subscribe to Panther-managed security content.',
  },
  [urls.contentCatalog.installed()]: {
    pageTitle: PageNameEnum.ContentCatalog,
    pageDescription: 'Browse and subscribe to Panther-managed security content.',
  },
  // settings menu pages
  [urls.settings.general.home()]: {
    pageTitle: PageNameEnum.SettingsGeneral,
  },
  [urls.settings.users()]: {
    pageTitle: PageNameEnum.SettingsUsers,
  },
  [urls.settings.roles.create()]: {
    pageTitle: PageNameEnum.SettingsUserRolesNew,
  },
  [urls.settings.roles.list()]: {
    pageTitle: PageNameEnum.SettingsUserRoles,
    documentationLink: USER_ROLES_DOC_URL,
  },
  [urls.settings.api.list()]: {
    pageTitle: PageNameEnum.SettingsApiTokens,
    documentationLink: PUBLIC_API_DOC_URL,
  },
  [urls.settings.api.create()]: {
    pageTitle: PageNameEnum.SettingsApiTokensNew,
  },
  [urls.settings.api.playground('')]: {
    pageTitle: PageNameEnum.SettingsApiPlayground,
    documentationLink: API_PLAYGROUND_DOC_URL,
  },
  [urls.settings.api.playground('graphql')]: {
    pageTitle: PageNameEnum.SettingsApiPlaygroundGraph,
    documentationLink: API_PLAYGROUND_DOC_URL,
  },
  [urls.settings.api.playground('rest')]: {
    pageTitle: PageNameEnum.SettingsApiPlaygroundRest,
    documentationLink: API_PLAYGROUND_DOC_URL,
  },
  [urls.account.support()]: {
    pageTitle: PageNameEnum.Support,
    pageDescription: 'Get the support you need',
    documentationLink: PANTHER_DOCS_LINK,
  },
  [urls.settings.alerts.contextTags()]: {
    pageTitle: PageNameEnum.SettingsAlertContextTags,
    pageDescription:
      'Manage context tags used to categorize and organize alerts across your organization.',
  },
};
